import React, { useEffect, useMemo, useState } from "react";
import { api, getCurrentUser } from "@/lib/api";
import { useFeatures } from "@/lib/features";
import { toast } from "sonner";
import {
  Calendar, Plus, X, Clock, Dog, User, Phone, ChevronLeft, ChevronRight,
  MessageCircle, Bell, CheckCircle2, Trash2, Edit3, Send, ExternalLink, Camera, Cake, Sparkles, History, PawPrint,
} from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import { PetsListModal, PetHistoryModal } from "@/components/PetHistory";

const STATUS_COLORS = {
  scheduled: { bg: "#3B82F6", text: "Prenotato" },
  confirmed: { bg: "#8B5CF6", text: "Confermato" },
  in_progress: { bg: "#F59E0B", text: "In corso" },
  completed: { bg: "#2F9E77", text: "Completato" },
  cancelled: { bg: "#94A3B8", text: "Annullato" },
  no_show: { bg: "#DC2626", text: "No-show" },
};

const PET_TYPES = [
  { id: "cane", label: "Cane" },
  { id: "gatto", label: "Gatto" },
  { id: "coniglio", label: "Coniglio" },
  { id: "altro", label: "Altro" },
];

// Slot 8:00-20:30 ogni 30 min
const TIME_SLOTS = (() => {
  const s = [];
  for (let h = 8; h < 21; h++) for (let m = 0; m < 60; m += 30) s.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  return s;
})();

const minutesOf = (hhmm) => {
  const [h, m] = String(hhmm || "00:00").split(":").map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
};

const hhmmOf = (mins) => {
  const h = Math.floor(mins / 60) % 24;
  const m = ((mins % 60) + 60) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const BLOCKS_DAY = new Set(["scheduled", "confirmed", "in_progress", "completed"]);

/** Fasce da 30 minuti coperte dall'appuntamento (es. 08:00 + 150 min → fino alle 10:30). */
const slotsCoveredBy = (appt) => {
  const start = (appt.start_at || "").slice(11, 16);
  const startMin = minutesOf(start);
  const dur = Math.max(1, parseInt(appt.duration_min, 10) || 30);
  const endMin = startMin + dur;
  return TIME_SLOTS.filter((slot) => {
    const s = minutesOf(slot);
    return s < endMin && s + 30 > startMin;
  });
};

const rangeLabel = (appt) => {
  const start = (appt.start_at || "").slice(11, 16);
  const dur = Math.max(1, parseInt(appt.duration_min, 10) || 30);
  return `${start}–${hhmmOf(minutesOf(start) + dur)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  } catch { return iso; }
};
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
function mondayOf(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const wd = d.getDay();
  d.setDate(d.getDate() + (wd === 0 ? -6 : 1 - wd));
  return toISODate(d);
}
function weekDays(monday) {
  return [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(monday, i));
}
const WEEKDAY_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const fmtWeekRange = (monday) => {
  try {
    const a = new Date(`${monday}T12:00:00`);
    const b = new Date(`${addDays(monday, 6)}T12:00:00`);
    const fa = a.toLocaleDateString("it-IT", { day: "numeric", month: "short" });
    const fb = b.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
    return `${fa} – ${fb}`;
  } catch {
    return monday;
  }
};

// Colori sede per il calendario multi-sede (assegnati per ordine di creazione)
const STORE_COLORS = ["#1E2D5B", "#2F9E77", "#F59E0B", "#8B5CF6", "#EC4899", "#0EA5E9", "#EF4444", "#14B8A6"];

function normPetType(t) {
  const s = String(t || "").trim().toLowerCase();
  if (s === "cane" || s === "gatto" || s === "coniglio" || s === "altro") return s;
  if (s.includes("gatt")) return "gatto";
  if (s.includes("conigl")) return "coniglio";
  if (s.includes("cane") || s === "dog") return "cane";
  return s ? "altro" : "cane";
}

function petSuggestKey(p) {
  return `${String(p.pet_name || "").trim().toLowerCase()}|${String(p.owner_phone || p.phone || "").replace(/\s+/g, "")}`;
}

function customerToPetSuggest(c) {
  return {
    pet_name: c.pet_name,
    pet_breed: c.pet_breed,
    pet_type: c.pet_type,
    pet_birth_date: c.pet_birthdate || c.pet_birth_date,
    pet_allergies: c.pet_allergies,
    pet_notes: c.pet_character || c.pet_notes,
    pet_behavior: c.pet_character,
    owner_name: c.owner_name || c.name,
    owner_phone: c.phone || c.owner_phone,
    owner_email: c.email,
    notes: c.notes || "",
    visits: null,
    from_clients: true,
  };
}

export default function Appointments() {
  const { hasFeature } = useFeatures();
  const [date, setDate] = useState(todayISO());
  const [appts, setAppts] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState("");

  const emptyForm = {
    pet_name: "",
    pet_breed: "",
    pet_type: "cane",
    pet_notes: "",
    pet_allergies: "",
    pet_cut_pref: "",
    pet_behavior: "",
    pet_birth_date: "",
    birthday_discount_enabled: null,  // null = eredita default tenant
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    owner_whatsapp_ok: true,
    owner_sms_ok: false,
    start_at: `${date}T10:00:00`,
    duration_min: 60,
    store_id: "",
    services: [],
    notes: "",
    status: "scheduled",
  };
  const [form, setForm] = useState(emptyForm);
  const [birthdays, setBirthdays] = useState([]);
  const [birthdayModalTarget, setBirthdayModalTarget] = useState(null);
  const [showPets, setShowPets] = useState(false);
  const [historyPet, setHistoryPet] = useState(null);
  const [knownPets, setKnownPets] = useState([]);
  const [custHits, setCustHits] = useState([]);
  const [hidePetSuggest, setHidePetSuggest] = useState(false);
  const [clientNoteMap, setClientNoteMap] = useState({});
  const [calView, setCalView] = useState("week");

  const weekMonday = useMemo(() => mondayOf(date), [date]);
  const weekDates = useMemo(() => weekDays(weekMonday), [weekMonday]);

  const load = () => {
    const storeQ = storeFilter ? `&store_id=${storeFilter}` : "";
    const url = calView === "week"
      ? `/appointments?kind=toelettatura&from_date=${weekMonday}&to_date=${addDays(weekMonday, 6)}T23:59:59${storeQ}`
      : `/appointments?kind=toelettatura&date=${date}${storeQ}`;
    return api.get(url).then((r) => setAppts(Array.isArray(r.data) ? r.data : [])).catch(() => setAppts([]));
  };
  const loadBirthdays = () => api.get("/appointments/birthdays/this-month").then(r => setBirthdays(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  useEffect(() => {
    api.get("/tenant/me").then(r => setTenant(r.data));
    api.get("/products").then(r => setServices((Array.isArray(r.data) ? r.data : []).filter(p => (p.duration_min || 0) > 0)));
    api.get("/stores").then(r => setStores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/customers", { params: { limit: 5000, mode: "toelettatura" } }).then((r) => {
      const map = {};
      for (const c of (Array.isArray(r.data) ? r.data : [])) {
        const note = String(c.notes || "").trim();
        const pet = String(c.pet_name || "").trim().toLowerCase();
        if (!note || !pet) continue;
        const phone = String(c.phone || "").replace(/\s+/g, "");
        const digits = phone.replace(/\D/g, "");
        map[`${pet}|${phone}`] = note;
        if (digits) map[`${pet}|${digits}`] = note;
      }
      setClientNoteMap(map);
    }).catch(() => {});
    loadBirthdays();
  }, []);
  useEffect(() => { load(); }, [date, storeFilter, calView]);

  useEffect(() => {
    if (!showForm) return;
    api.get("/pets").then((r) => setKnownPets(Array.isArray(r.data) ? r.data : [])).catch(() => setKnownPets([]));
  }, [showForm]);

  useEffect(() => {
    if (!showForm) return;
    const q = (form.pet_name || "").trim();
    if (q.length < 2) {
      setCustHits([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/customers", { params: { q, limit: 30, mode: "toelettatura" } })
        .then((r) => setCustHits((Array.isArray(r.data) ? r.data : []).filter((c) => (c.pet_name || "").trim())))
        .catch(() => setCustHits([]));
    }, 200);
    return () => clearTimeout(t);
  }, [showForm, form.pet_name]);

  // Sedi visibili all'operatore (store_ids limitati) + colori per sede
  const me = getCurrentUser();
  const storeList = Array.isArray(stores) ? stores : [];
  const myStoreIds = Array.isArray(me?.store_ids) ? me.store_ids : [];
  const myStores = myStoreIds.length ? storeList.filter(s => myStoreIds.includes(s.id)) : storeList;
  const storeColorOf = (sid) => {
    const s = storeList.find((x) => x.id === sid);
    if (s?.color && String(s.color).startsWith("#")) return s.color;
    const i = storeList.findIndex((x) => x.id === sid);
    return i >= 0 ? STORE_COLORS[i % STORE_COLORS.length] : "#94A3B8";
  };
  const bookedByOf = (a) => a.booked_by_name || a.created_by_name || "";
  const storeNameOf = (sid) => storeList.find(s => s.id === sid)?.name || null;
  const canMultiStore = storeList.length > 1 || hasFeature("multi_store") || hasFeature("appointments_multistore");
  const canWhatsApp = hasFeature("whatsapp_reminders") || hasFeature("reminder_wa");

  const clientNoteFor = (row) => {
    const pet = String(row?.pet_name || "").trim().toLowerCase();
    if (!pet) return "";
    const phone = String(row?.owner_phone || row?.customer_phone || row?.phone || "").replace(/\s+/g, "");
    const digits = phone.replace(/\D/g, "");
    return clientNoteMap[`${pet}|${phone}`] || (digits ? clientNoteMap[`${pet}|${digits}`] : "") || "";
  };
  const visibleNote = (row) => clientNoteFor(row) || String(row?.notes || "").trim();

  const openNew = (slot, dayIso) => {
    const day = dayIso || date;
    setEditing(null);
    setForm({ ...emptyForm, start_at: `${day}T${slot || "10:00"}:00`, store_id: storeFilter || myStores.find(s => s.is_main)?.id || myStores[0]?.id || "" });
    setHidePetSuggest(false);
    setShowForm(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      pet_name: a.pet_name || "",
      pet_breed: a.pet_breed || "",
      pet_type: a.pet_type || "cane",
      pet_notes: a.pet_notes || "",
      pet_allergies: a.pet_allergies || "",
      pet_cut_pref: a.pet_cut_pref || "",
      pet_behavior: a.pet_behavior || "",
      pet_birth_date: a.pet_birth_date || "",
      birthday_discount_enabled: a.birthday_discount_enabled ?? null,
      owner_name: a.owner_name || a.customer_name || "",
      owner_phone: a.owner_phone || a.customer_phone || "",
      owner_email: a.owner_email || "",
      owner_whatsapp_ok: a.owner_whatsapp_ok ?? true,
      owner_sms_ok: a.owner_sms_ok ?? false,
      start_at: a.start_at,
      duration_min: a.duration_min || 60,
      store_id: a.store_id || "",
      services: Array.isArray(a.services) ? a.services : [],
      notes: a.notes || "",
      status: a.status || "scheduled",
    });
    setHidePetSuggest(true);
    setShowForm(true);
  };

  const pickKnownPet = (p) => {
    setForm((f) => ({
      ...f,
      pet_name: p.pet_name || "",
      pet_breed: p.pet_breed || "",
      pet_type: normPetType(p.pet_type),
      pet_notes: p.pet_notes || "",
      pet_allergies: p.pet_allergies || "",
      pet_cut_pref: p.pet_cut_pref || "",
      pet_behavior: p.pet_behavior || "",
      pet_birth_date: (p.pet_birth_date || p.pet_birthdate || "").slice(0, 10),
      owner_name: p.owner_name || "",
      owner_phone: p.owner_phone || p.phone || "",
      owner_email: p.owner_email || p.email || f.owner_email || "",
      notes: (f.notes || "").trim() ? f.notes : (p.notes || clientNoteFor(p) || ""),
    }));
    setHidePetSuggest(true);
  };

  const petSuggests = useMemo(() => {
    const q = (form.pet_name || "").trim().toLowerCase();
    if (q.length < 2) return [];
    const map = new Map();
    for (const p of knownPets) {
      const name = (p.pet_name || "").toLowerCase();
      if (!name.includes(q)) continue;
      map.set(petSuggestKey(p), p);
    }
    for (const c of custHits) {
      const row = customerToPetSuggest(c);
      const name = (row.pet_name || "").toLowerCase();
      if (!name.includes(q)) continue;
      const k = petSuggestKey(row);
      const prev = map.get(k);
      if (!prev) map.set(k, row);
      else if (!(prev.notes || "").trim() && (row.notes || "").trim()) map.set(k, { ...prev, notes: row.notes });
    }
    return [...map.values()].slice(0, 12);
  }, [form.pet_name, knownPets, custHits, clientNoteMap]);

  const save = async () => {
    if (!form.pet_name.trim() && !form.owner_name.trim()) return toast.error("Inserisci almeno il nome dell'animale o del proprietario");
    try {
      const duration = parseInt(form.duration_min, 10);
      const payload = {
        ...form,
        notes: (form.notes || "").trim() || clientNoteFor(form) || "",
        duration_min: duration > 0 ? duration : 60,
        store_id: form.store_id || null,
        kind: "toelettatura",
      };
      if (editing) {
        await api.patch(`/appointments/${editing.id}`, payload);
        toast.success("Appuntamento aggiornato");
      } else {
        await api.post("/appointments", payload);
        toast.success("Appuntamento creato");
      }
      setShowForm(false);
      load();
      loadBirthdays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Eliminare l'appuntamento di ${a.pet_name || a.owner_name}?`)) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const changeStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}/status?status=${status}`);
      load();
    } catch { toast.error("Errore"); }
  };

  const toggleService = (svc) => {
    const list = Array.isArray(form.services) ? form.services : [];
    const isIn = list.find((s) => (svc.id && s.product_id === svc.id) || s.name === svc.name);
    if (isIn) {
      const next = list.filter((s) => !((svc.id && s.product_id === svc.id) || s.name === svc.name));
      const totalDuration = next.reduce((s, x) => s + (x.duration_min || 0), 0);
      setForm({ ...form, services: next, duration_min: totalDuration || form.duration_min });
    } else {
      const newSvcs = [...list, { name: svc.name, price: svc.price, duration_min: svc.duration_min, product_id: svc.id }];
      const totalDuration = newSvcs.reduce((s, x) => s + (x.duration_min || 0), 0);
      setForm({ ...form, services: newSvcs, duration_min: totalDuration || form.duration_min });
    }
  };

  const openReminder = async (a) => {
    try {
      const r = await api.post(`/appointments/${a.id}/reminder-preview`, {});
      setReminderTarget({ appt: a, ...r.data });
    } catch { toast.error("Errore anteprima reminder"); }
  };
  const sendReminderNow = async (channel) => {
    if (!reminderTarget) return;
    try {
      const r = await api.post(`/appointments/${reminderTarget.appt.id}/send-reminder`, { channel, force: reminderTarget.appt.reminder_sent_at ? true : false });
      if (r.data.whatsapp_url) {
        window.open(r.data.whatsapp_url, "_blank");
        toast.success("Apertura WhatsApp — tocca 'Invia'");
      } else {
        toast.success(`Reminder inviato via ${r.data.provider}`);
      }
      setReminderTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio reminder");
    }
  };

  const sendBirthdayNow = async (appointmentId, channel, force = false) => {
    try {
      const r = await api.post(`/appointments/${appointmentId}/send-birthday-message`, { channel, force });
      if (r.data.whatsapp_url) {
        window.open(r.data.whatsapp_url, "_blank");
        toast.success("Apertura WhatsApp — tocca 'Invia'");
      } else {
        toast.success(`Auguri inviati via ${r.data.provider}`);
      }
      setBirthdayModalTarget(null);
      loadBirthdays();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    }
  };
  const openBirthdayModal = async (pet) => {
    try {
      const r = await api.get(`/appointments/${pet.appointment_id}/birthday-status`);
      setBirthdayModalTarget({ pet, ...r.data });
    } catch { toast.error("Errore anteprima"); }
  };

  const apptsByDay = useMemo(() => {
    const m = {};
    for (const d of weekDates) m[d] = [];
    for (const a of (Array.isArray(appts) ? appts : [])) {
      const d = (a.start_at || "").slice(0, 10);
      if (!m[d]) m[d] = [];
      m[d].push(a);
    }
    return m;
  }, [appts, weekDates]);

  const goPrev = () => setDate(calView === "week" ? addDays(weekMonday, -7) : addDays(date, -1));
  const goNext = () => setDate(calView === "week" ? addDays(weekMonday, 7) : addDays(date, 1));
  const openDay = (d) => { setDate(d); setCalView("day"); };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-2">Toelettatura</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
            <Dog className="w-8 h-8 text-velta-ink" /> Appuntamenti
          </h1>
          <p className="text-velta-mute mt-1">Calendario pet-first: settimana per saturazione, giorno per lavorare gli slot.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex rounded-md border border-velta-border overflow-hidden h-10">
            <button
              type="button"
              data-testid="appt-view-week"
              onClick={() => setCalView("week")}
              className={`px-3 text-xs font-medium ${calView === "week" ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
            >
              Settimana
            </button>
            <button
              type="button"
              data-testid="appt-view-day"
              onClick={() => setCalView("day")}
              className={`px-3 text-xs font-medium ${calView === "day" ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
            >
              Giorno
            </button>
          </div>
          <button
            data-testid="appt-date-prev"
            onClick={goPrev}
            className="h-10 w-10 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"
          ><ChevronLeft className="w-4 h-4" /></button>
          {calView === "day" ? (
            <input
              data-testid="appt-date-picker"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 px-3 border border-velta-border rounded-md text-sm"
            />
          ) : (
            <div data-testid="appt-week-label" className="h-10 px-3 border border-velta-border rounded-md text-sm flex items-center font-medium min-w-[11rem] justify-center">
              {fmtWeekRange(weekMonday)}
            </div>
          )}
          <button
            data-testid="appt-date-next"
            onClick={goNext}
            className="h-10 w-10 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"
          ><ChevronRight className="w-4 h-4" /></button>
          <button
            type="button"
            data-testid="appt-today-btn"
            onClick={() => setDate(todayISO())}
            className="h-10 px-3 border border-velta-border rounded-md text-xs font-medium hover:border-velta-ink"
          >
            Oggi
          </button>
          <button
            data-testid="appt-pets-btn"
            onClick={() => setShowPets(true)}
            className="h-10 px-4 border border-velta-border rounded-md text-sm font-medium hover:border-velta-ink inline-flex items-center gap-2"
          >
            <PawPrint className="w-4 h-4" /> Animali
          </button>
          <button
            data-testid="appt-new-btn"
            onClick={() => openNew("10:00")}
            className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium hover:bg-[#162244] inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuovo appuntamento
          </button>
        </div>
      </div>

      <div className="mb-4 text-sm font-medium text-velta-ink capitalize">
        {calView === "week" ? `Settimana ${fmtWeekRange(weekMonday)}` : fmtDate(date)}
      </div>

      {/* Filtro sedi (calendario multi-sede: ogni punto vendita ha un colore) */}
      {canMultiStore && storeList.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="appt-store-filter">
          <button
            data-testid="appt-store-all"
            onClick={() => setStoreFilter("")}
            className={`h-9 px-3 rounded-full text-xs font-medium border transition ${!storeFilter ? "bg-velta-ink text-white border-velta-ink" : "bg-white border-velta-border hover:border-velta-ink"}`}
          >
            Tutte le sedi
          </button>
          {(Array.isArray(myStores) ? myStores : []).map((s) => (
            <button
              key={s.id}
              data-testid={`appt-store-${s.id}`}
              onClick={() => setStoreFilter(s.id)}
              className={`h-9 px-3 rounded-full text-xs font-medium border transition inline-flex items-center gap-1.5 ${storeFilter === s.id ? "text-white" : "bg-white border-velta-border hover:border-velta-ink"}`}
              style={storeFilter === s.id ? { backgroundColor: storeColorOf(s.id), borderColor: storeColorOf(s.id) } : {}}
            >
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: storeColorOf(s.id) }} />
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Widget Compleanni questo mese */}
      {birthdays.length > 0 && (
        <div className="mb-4 bg-gradient-to-r from-pink-50 to-purple-50 border-2 border-[#EC4899]/30 rounded-md p-4" data-testid="appt-birthdays-widget">
          <div className="flex items-center gap-2 mb-3">
            <Cake className="w-5 h-5 text-[#EC4899]" />
            <h3 className="font-heading font-semibold text-[#DB2777]">Compleanni questo mese ({birthdays.length})</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(Array.isArray(birthdays) ? birthdays : []).map((b) => (
              <button
                key={b.appointment_id}
                data-testid={`appt-birthday-${b.appointment_id}`}
                onClick={() => openBirthdayModal(b)}
                className={`text-left border-2 rounded-md px-3 py-2 bg-white transition ${b.enabled ? "border-[#EC4899] hover:shadow-md" : "border-velta-border opacity-70"}`}
              >
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  <Dog className="w-3.5 h-3.5" /> {b.pet_name}
                  {b.pet_breed && <span className="text-[11px] font-normal text-velta-mute">· {b.pet_breed}</span>}
                </div>
                <div className="text-[11px] text-velta-mute flex items-center gap-1 mt-0.5">
                  <User className="w-3 h-3" /> {b.owner_name} · {b.pet_birth_date?.slice(5)}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${b.enabled ? "bg-[#EC4899] text-white" : "bg-velta-mute/20 text-velta-mute"}`}>
                    {b.enabled ? "Sconto ON" : "Sconto OFF"}
                  </span>
                  {b.already_sent_this_year && <span className="text-[9px] font-mono text-velta-success">✓ Inviato</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline slot / griglia settimana */}
      {calView === "week" ? (
        <div className="bg-white border border-velta-border rounded-md overflow-x-auto" data-testid="appt-week-grid">
          <div className="grid grid-cols-7 min-w-[840px] divide-x divide-velta-border">
            {weekDates.map((d, i) => {
              const items = apptsByDay[d] || [];
              const isToday = d === todayISO();
              const isSelected = d === date;
              return (
                <div key={d} className={`min-h-[420px] flex flex-col ${isToday ? "bg-amber-50/40" : ""}`} data-testid={`appt-week-col-${d}`}>
                  <button
                    type="button"
                    data-testid={`appt-week-day-${d}`}
                    onClick={() => openDay(d)}
                    className={`px-2 py-2.5 text-left border-b border-velta-border sticky top-0 ${isSelected ? "bg-velta-ink text-white" : "bg-velta-bg hover:bg-velta-bg/80"}`}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">{WEEKDAY_SHORT[i]}</div>
                    <div className="font-heading font-semibold text-lg leading-tight">{d.slice(8, 10)}</div>
                    <div className="text-[10px] font-mono mt-0.5 opacity-80">{items.length} app.</div>
                  </button>
                  <div className="flex-1 p-1.5 space-y-1.5">
                    {(Array.isArray(items) ? items : []).map((a) => {
                      const s = STATUS_COLORS[a.status] || STATUS_COLORS.scheduled;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          data-testid={`appt-week-card-${a.id}`}
                          onClick={() => openEdit(a)}
                          className="w-full text-left rounded border px-1.5 py-1.5 hover:border-velta-ink transition"
                          style={{
                            borderColor: a.store_id ? storeColorOf(a.store_id) : s.bg,
                            borderLeftWidth: 4,
                          }}
                        >
                          <div className="text-[10px] font-mono text-velta-mute">{rangeLabel(a)} · {a.duration_min || 30} min</div>
                          <div className="text-xs font-semibold truncate leading-tight">{a.pet_name || a.owner_name || "—"}</div>
                          {a.owner_name && a.pet_name && (
                            <div className="text-[10px] text-velta-mute truncate">{a.owner_name}</div>
                          )}
                          {visibleNote(a) && (
                            <div className="text-[10px] text-velta-ink truncate" title={visibleNote(a)}>Note: {visibleNote(a)}</div>
                          )}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      data-testid={`appt-week-add-${d}`}
                      onClick={() => openNew("10:00", d)}
                      className="w-full h-8 border border-dashed border-velta-border/70 rounded text-[10px] text-velta-mute hover:border-velta-ink hover:text-velta-ink"
                    >
                      + Prenota
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
      <div className="bg-white border border-velta-border rounded-md overflow-hidden">
        {(() => {
          const SLOT_PX = 104;
          const list = Array.isArray(appts) ? appts : [];
          const coveredSlots = new Set();
          const placed = [];
          list.forEach((a) => {
            const covered = slotsCoveredBy(a);
            const start = covered[0];
            if (!start) return;
            const blocking = BLOCKS_DAY.has(a.status);
            if (blocking) covered.forEach((s) => coveredSlots.add(s));
            const idx = TIME_SLOTS.indexOf(start);
            if (idx < 0) return;
            placed.push({ a, idx, span: blocking ? Math.max(1, covered.length) : 1 });
          });
          const bands = {};
          placed.forEach((p) => { (bands[p.idx] = bands[p.idx] || []).push(p); });
          return (
            <div className="grid grid-cols-[80px_1fr]">
              <div>
                {TIME_SLOTS.map((slot, i) => (
                  <div key={slot} className={`h-[104px] px-3 pt-3 text-xs font-mono text-velta-mute border-r border-b border-velta-border/60 ${coveredSlots.has(slot) ? "bg-[#EEF2FF]" : i % 2 ? "bg-velta-bg/40" : "bg-white"}`}>{slot}</div>
                ))}
              </div>
              <div className="relative">
                {TIME_SLOTS.map((slot, i) => (
                  <div key={slot} className={`h-[104px] border-b border-velta-border/60 p-2 ${coveredSlots.has(slot) ? "bg-[#EEF2FF]" : i % 2 ? "bg-velta-bg/40" : "bg-white"}`}>
                    {!coveredSlots.has(slot) && (
                      <button
                        onClick={() => openNew(slot)}
                        data-testid={`appt-slot-empty-${slot}`}
                        className="w-full h-10 border border-dashed border-velta-border/60 rounded text-[11px] text-velta-mute/50 hover:border-velta-ink hover:text-velta-ink transition"
                      >
                        + Prenota
                      </button>
                    )}
                  </div>
                ))}
                {Object.values(bands).map((group) => group.map((p, k) => {
                  const a = p.a;
                  const s = STATUS_COLORS[a.status] || STATUS_COLORS.scheduled;
                  const currentMonth = String(new Date().getMonth() + 1).padStart(2, "0");
                  const isBirthdayMonth = a.pet_birth_date && a.pet_birth_date.slice(5, 7) === currentMonth;
                  const n = group.length;
                  return (
                    <div
                      key={a.id}
                      data-testid={`appt-card-${a.id}`}
                      className="absolute bg-white border-2 rounded-md p-2.5 shadow-sm overflow-y-auto"
                      style={{
                        top: p.idx * SLOT_PX + 6,
                        height: p.span * SLOT_PX - 12,
                        left: `calc(${(k / n) * 100}% + 8px)`,
                        width: `calc(${100 / n}% - 16px)`,
                        zIndex: 2,
                        borderColor: a.store_id ? storeColorOf(a.store_id) : s.bg,
                        ...(a.store_id ? { borderLeftWidth: 8, borderLeftColor: storeColorOf(a.store_id) } : {}),
                      }}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0">
                          {/* Pet-first: nome animale grande */}
                          <div className="text-[11px] font-mono font-semibold text-velta-ink mb-1">{rangeLabel(a)} · {a.duration_min || 30} min</div>
                          <div className="font-heading font-semibold text-base flex items-center gap-1.5 leading-tight">
                            <Dog className="w-4 h-4 shrink-0" />
                            <span className="truncate">{a.pet_name || "—"}</span>
                            {isBirthdayMonth && (
                              <span className="text-[9px] font-mono uppercase font-bold px-1.5 py-0.5 rounded bg-[#EC4899] text-white flex items-center gap-0.5 shrink-0" title="Compleanno questo mese">
                                <Cake className="w-2.5 h-2.5" /> BDay
                              </span>
                            )}
                          </div>
                          {a.pet_breed && (
                            <div className="text-xs text-velta-mute leading-tight mt-0.5">{a.pet_breed}{a.pet_type && a.pet_type !== "cane" ? ` · ${a.pet_type}` : ""}</div>
                          )}
                          {/* Proprietario piccolo sotto */}
                          <div className="text-[11px] text-velta-mute flex items-center gap-1 mt-1">
                            <User className="w-3 h-3" /> {a.owner_name || a.customer_name || "—"}
                            {(a.owner_phone || a.customer_phone) && <> · <Phone className="w-3 h-3" /> {a.owner_phone || a.customer_phone}</>}
                          </div>
                          {a.store_id && storeNameOf(a.store_id) && (
                            <div className="text-[10px] font-semibold mt-1 inline-flex items-center gap-1" style={{ color: storeColorOf(a.store_id) }} data-testid={`appt-card-store-${a.id}`}>
                              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: storeColorOf(a.store_id) }} /> {storeNameOf(a.store_id)}
                            </div>
                          )}
                          {bookedByOf(a) && (
                            <div className="text-[10px] text-velta-mute mt-1" data-testid={`appt-card-bookedby-${a.id}`}>
                              Prenotato da {bookedByOf(a)}
                            </div>
                          )}
                          {visibleNote(a) && (
                            <div className="text-[11px] text-velta-ink mt-1 line-clamp-3" data-testid={`appt-card-notes-${a.id}`}>
                              Note: {visibleNote(a)}
                            </div>
                          )}
                        </div>
                        <span className="text-[9px] font-mono uppercase font-bold px-1.5 py-0.5 rounded text-white shrink-0" style={{ background: s.bg }}>{s.text}</span>
                      </div>
                      {a.services && a.services.length > 0 && (
                        <div className="text-[11px] text-velta-ink border-t border-velta-border pt-1.5 mt-1.5">
                          {(Array.isArray(a.services) ? a.services : []).map(sv => sv.name).join(" · ")}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-velta-border/60">
                        <button data-testid={`appt-edit-${a.id}`} onClick={() => openEdit(a)} title="Modifica" className="h-7 w-7 rounded hover:bg-velta-bg flex items-center justify-center">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        {a.pet_name && (
                          <button data-testid={`appt-history-${a.id}`} onClick={() => setHistoryPet({ name: a.pet_name, phone: a.owner_phone || a.customer_phone || "" })} title="Storico animale" className="h-7 w-7 rounded hover:bg-velta-bg flex items-center justify-center text-[#2D5594]">
                            <History className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canWhatsApp && (
                        <button data-testid={`appt-reminder-${a.id}`} onClick={() => openReminder(a)} title="Invia reminder" className="h-7 w-7 rounded hover:bg-velta-bg flex items-center justify-center text-[#2F9E77]">
                          <Bell className="w-3.5 h-3.5" />
                        </button>
                        )}
                        {a.status !== "completed" && (
                          <button data-testid={`appt-done-${a.id}`} onClick={() => changeStatus(a, "completed")} title="Segna come completato" className="h-7 w-7 rounded hover:bg-velta-bg flex items-center justify-center text-velta-success">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button data-testid={`appt-del-${a.id}`} onClick={() => del(a)} title="Elimina" className="h-7 w-7 rounded hover:bg-red-50 flex items-center justify-center text-velta-error ml-auto">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {a.reminder_sent_at && (
                          <span className="text-[9px] font-mono text-velta-success ml-1" title={`Reminder inviato: ${a.reminder_sent_at}`}>✓ WA</span>
                        )}
                        {a.photos && a.photos.length > 0 && (
                          <span className="text-[9px] font-mono text-velta-mute inline-flex items-center gap-0.5" title={`${a.photos.length} foto`}>
                            <Camera className="w-3 h-3" />{a.photos.length}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                }))}
              </div>
            </div>
          );
        })()}
      </div>
      )}

      {/* Modal Nuovo/Modifica */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-velta-border flex items-center justify-between">
              <h3 className="font-heading text-2xl font-semibold">{editing ? "Modifica appuntamento" : "Nuovo appuntamento"}</h3>
              <button onClick={() => setShowForm(false)} data-testid="appt-form-close" className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Sezione ANIMALE (PRIMARIA) */}
              <div className="border-2 border-velta-ink/20 rounded-md p-4 bg-velta-bg/30">
                <div className="text-[10px] font-mono uppercase tracking-widest text-velta-ink font-bold mb-3 flex items-center gap-1.5"><Dog className="w-3.5 h-3.5" /> Dati animale</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Nome animale *</label>
                    <input
                      data-testid="appt-form-petname"
                      value={form.pet_name}
                      onChange={(e) => { setHidePetSuggest(false); setForm({ ...form, pet_name: e.target.value }); }}
                      onFocus={() => setHidePetSuggest(false)}
                      placeholder="Bruno"
                      autoComplete="off"
                      className="w-full h-11 px-3 border border-velta-border rounded-md text-base font-semibold outline-none focus:border-velta-ink"
                    />
                    {!hidePetSuggest && petSuggests.length > 0 && (
                      <div data-testid="appt-pet-suggest" className="absolute z-20 left-0 right-0 mt-1 bg-white border border-velta-border rounded-md shadow-lg max-h-56 overflow-y-auto">
                        <div className="px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-velta-mute border-b border-velta-border">
                          {petSuggests.length === 1 ? "1 animale già in anagrafe — scegli per compilare" : `${petSuggests.length} omonimi — scegli proprietario e telefono`}
                        </div>
                        {petSuggests.map((p, i) => (
                          <button
                            key={petSuggestKey(p) + i}
                            type="button"
                            data-testid={`appt-pet-suggest-${i}`}
                            onClick={() => pickKnownPet(p)}
                            className="w-full text-left px-3 py-2.5 hover:bg-velta-bg border-b border-velta-border/60 last:border-0"
                          >
                            <div className="font-semibold text-sm flex items-center gap-1.5">
                              <Dog className="w-3.5 h-3.5 shrink-0" />
                              {p.pet_name}
                              {p.pet_breed && <span className="font-normal text-velta-mute">· {p.pet_breed}</span>}
                            </div>
                            <div className="text-[11px] text-velta-mute mt-0.5 flex items-center gap-1">
                              <User className="w-3 h-3" /> {p.owner_name || "—"}
                              {(p.owner_phone || p.phone) && <><span>·</span><Phone className="w-3 h-3" /> {p.owner_phone || p.phone}</>}
                              {p.visits != null && <span className="ml-auto font-mono">{p.visits} vis.</span>}
                            </div>
                            {(p.notes || clientNoteFor(p)) && <div className="text-[10px] text-velta-ink mt-0.5 truncate">Note: {p.notes || clientNoteFor(p)}</div>}
                            {p.pet_allergies && <div className="text-[10px] text-red-800 mt-0.5">Allergie: {p.pet_allergies}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Razza</label>
                    <input
                      data-testid="appt-form-petbreed"
                      value={form.pet_breed}
                      onChange={(e) => setForm({ ...form, pet_breed: e.target.value })}
                      placeholder="Golden Retriever"
                      className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Tipo</label>
                    <select
                      data-testid="appt-form-pettype"
                      value={form.pet_type}
                      onChange={(e) => setForm({ ...form, pet_type: e.target.value })}
                      className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                    >
                      {(Array.isArray(PET_TYPES) ? PET_TYPES : []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Note animale (allergie, carattere)</label>
                    <input
                      data-testid="appt-form-petnotes"
                      value={form.pet_notes}
                      onChange={(e) => setForm({ ...form, pet_notes: e.target.value })}
                      placeholder="Pauroso, no forbici"
                      className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Allergie</label>
                    <input data-testid="appt-form-allergies" value={form.pet_allergies || ""} onChange={(e) => setForm({ ...form, pet_allergies: e.target.value })} placeholder="Es. shampoo, forbici calde" className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Preferenze di taglio</label>
                    <input data-testid="appt-form-cut" value={form.pet_cut_pref || ""} onChange={(e) => setForm({ ...form, pet_cut_pref: e.target.value })} placeholder="Es. teddy, corto sul dorso" className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Note comportamentali</label>
                    <input data-testid="appt-form-behavior" value={form.pet_behavior || ""} onChange={(e) => setForm({ ...form, pet_behavior: e.target.value })} placeholder="Mordicchia, sta fermo sul tavolo…" className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink" />
                  </div>
                  {/* Compleanno & Sconto */}
                  <div className="col-span-2 border-t border-velta-border/60 pt-3 mt-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-velta-ink font-bold mb-2 flex items-center gap-1.5">
                      <Cake className="w-3.5 h-3.5 text-[#EC4899]" /> Compleanno & sconto
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Data di nascita animale</label>
                        <input
                          data-testid="appt-form-birthdate"
                          type="date"
                          value={form.pet_birth_date}
                          onChange={(e) => setForm({ ...form, pet_birth_date: e.target.value })}
                          className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Sconto compleanno</label>
                        <select
                          data-testid="appt-form-birthday-toggle"
                          value={form.birthday_discount_enabled === null ? "default" : (form.birthday_discount_enabled ? "on" : "off")}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm({ ...form, birthday_discount_enabled: v === "default" ? null : v === "on" });
                          }}
                          className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink bg-white"
                        >
                          <option value="default">Predefinito tenant ({tenant?.notification_config?.birthday_default_enabled ? "attivo" : "disattivo"})</option>
                          <option value="on">Attivo per questo pet</option>
                          <option value="off">Disattivo per questo pet</option>
                        </select>
                      </div>
                    </div>
                    {form.pet_birth_date && (
                      <div className="text-[11px] text-velta-mute mt-2 flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Se il flag è attivo e siamo nel mese di nascita, il POS applicherà automaticamente lo sconto {tenant?.notification_config?.birthday_discount_pct ?? 10}% e comparirà un bottone per inviare gli auguri.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sezione PROPRIETARIO */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Nome proprietario</label>
                  <input
                    data-testid="appt-form-owner"
                    value={form.owner_name}
                    onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                    placeholder="Mario Rossi"
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Telefono</label>
                  <input
                    data-testid="appt-form-phone"
                    value={form.owner_phone}
                    onChange={(e) => setForm({ ...form, owner_phone: e.target.value })}
                    placeholder="+39 340 1234567"
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink font-mono"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-4 text-xs">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.owner_whatsapp_ok} onChange={(e) => setForm({ ...form, owner_whatsapp_ok: e.target.checked })} data-testid="appt-form-wa-ok" />
                    <span>Consenso WhatsApp</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.owner_sms_ok} onChange={(e) => setForm({ ...form, owner_sms_ok: e.target.checked })} data-testid="appt-form-sms-ok" />
                    <span>Consenso SMS</span>
                  </label>
                </div>
              </div>

              {/* Sede (multi-sede: menu a tendina con tutte le sedi memorizzate) */}
              {stores.length > 0 && (
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Sede / Punto vendita</label>
                  <select
                    data-testid="appt-form-store"
                    value={form.store_id || ""}
                    onChange={(e) => setForm({ ...form, store_id: e.target.value })}
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink bg-white"
                  >
                    <option value="">— Nessuna sede specifica —</option>
                    {(Array.isArray(myStores) ? myStores : []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}

              {/* Data/ora */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Data & ora</label>
                  <input
                    data-testid="appt-form-start"
                    type="datetime-local"
                    value={form.start_at.slice(0, 16)}
                    onChange={(e) => setForm({ ...form, start_at: e.target.value + ":00" })}
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Durata (min)</label>
                  <input
                    data-testid="appt-form-duration"
                    type="text"
                    inputMode="numeric"
                    value={form.duration_min}
                    onChange={(e) => setForm({ ...form, duration_min: e.target.value.replace(/[^\d]/g, "") })}
                    onBlur={() => {
                      const n = parseInt(form.duration_min, 10);
                      if (!n) setForm((f) => ({ ...f, duration_min: 60 }));
                    }}
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Stato</label>
                  <select
                    data-testid="appt-form-status"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-velta-ink"
                  >
                    {Object.entries(STATUS_COLORS).map(([k, v]) => <option key={k} value={k}>{v.text}</option>)}
                  </select>
                </div>
              </div>

              {/* Servizi */}
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2">Servizi ({(Array.isArray(form.services) ? form.services : []).length} selezionati)</label>
                <div className="flex flex-wrap gap-2">
                  {services.length === 0 && <p className="text-xs text-velta-mute">Nessun servizio disponibile. Imposta la &quot;Durata servizio (min)&quot; nella scheda articolo per usarlo qui.</p>}
                  {(Array.isArray(services) ? services : []).map(s => {
                    const sel = (Array.isArray(form.services) ? form.services : []).find(x => x.name === s.name);
                    return (
                      <button
                        key={s.id}
                        data-testid={`appt-form-svc-${s.id}`}
                        onClick={() => toggleService(s)}
                        className={`text-xs px-3 h-9 rounded-md border-2 transition ${sel ? "border-velta-ink bg-velta-ink text-white" : "border-velta-border hover:border-velta-ink"}`}
                      >
                        {s.name} · {s.price?.toFixed(2)}€
                        {s.duration_min && <span className="ml-1 opacity-70">({s.duration_min}′)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Foto Pre/Post (solo se editing esistente — serve id) */}
              {editing && (
                <div className="border border-velta-border rounded-md p-4 bg-velta-bg/30">
                  <PhotoUploader appointmentId={editing.id} />
                </div>
              )}

              {/* Note */}
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Note cliente</label>
                <textarea
                  data-testid="appt-form-notes"
                  rows={2}
                  value={form.notes || clientNoteFor(form)}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Note salvate sul cliente"
                  className="w-full p-3 border border-velta-border rounded-md text-sm outline-none focus:border-velta-ink resize-none"
                />
              </div>
            </div>

            <div className="p-6 border-t border-velta-border flex items-center gap-2">
              <button data-testid="appt-form-cancel" onClick={() => setShowForm(false)} className="flex-1 h-11 border border-velta-border rounded-md font-medium">Annulla</button>
              <button data-testid="appt-form-save" onClick={save} className="flex-1 h-11 bg-velta-ink text-white rounded-md font-medium btn-tactile">{editing ? "Aggiorna" : "Crea appuntamento"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reminder */}
      {reminderTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setReminderTarget(null)}>
          <div className="bg-white rounded-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-velta-border flex items-center justify-between">
              <h3 className="font-heading text-2xl font-semibold flex items-center gap-2"><Bell className="w-6 h-6 text-[#2F9E77]" /> Invia reminder</h3>
              <button onClick={() => setReminderTarget(null)} className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-xs text-velta-mute">
                <div>Provider tenant: <strong className="text-velta-ink">{reminderTarget.provider}</strong></div>
                <div>Destinatario: <strong className="text-velta-ink">{reminderTarget.appt.owner_phone || reminderTarget.appt.customer_phone || "— (nessun telefono)"}</strong></div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Anteprima messaggio</label>
                <div className="bg-velta-bg border border-velta-border rounded-md p-3 text-sm font-mono whitespace-pre-line" data-testid="appt-reminder-preview">
                  {reminderTarget.message}
                </div>
                {reminderTarget.last_photo_url && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-velta-mute">
                    <Camera className="w-3.5 h-3.5" />
                    <span>Include foto ultima visita:</span>
                    <img src={reminderTarget.last_photo_url} alt="ultima" className="h-12 w-12 object-cover rounded border border-velta-border" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="appt-reminder-send-auto"
                  onClick={() => sendReminderNow("auto")}
                  className="h-11 bg-velta-ink text-white rounded-md font-medium btn-tactile inline-flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Invia con provider attivo
                </button>
                {reminderTarget.whatsapp_url && (
                  <a
                    data-testid="appt-reminder-wa-link"
                    href={reminderTarget.whatsapp_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => sendReminderNow("wa_deeplink")}
                    className="h-11 bg-[#25D366] text-white rounded-md font-medium btn-tactile inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" /> Apri WhatsApp <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {reminderTarget.appt.reminder_sent_at && (
                <div className="text-[11px] text-velta-success">✓ Reminder già inviato — verrà rispedito con force=true</div>
              )}
              <div className="text-[11px] text-velta-mute pt-2 border-t border-velta-border">
                Puoi configurare provider (Meta / Twilio / Manuale) e template in <a href="/dashboard/branding" className="underline text-velta-ink">Branding → Notifiche</a>.
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modal Auguri Compleanno */}
      {birthdayModalTarget && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setBirthdayModalTarget(null)}>
          <div className="bg-white rounded-md w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-velta-border flex items-center justify-between">
              <h3 className="font-heading text-2xl font-semibold flex items-center gap-2"><Cake className="w-6 h-6 text-[#EC4899]" /> Auguri di compleanno</h3>
              <button onClick={() => setBirthdayModalTarget(null)} className="text-velta-mute hover:text-velta-ink"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-velta-bg rounded p-3 text-xs">
                <div><strong className="text-velta-ink">{birthdayModalTarget.pet.pet_name}</strong> {birthdayModalTarget.pet.pet_breed && `· ${birthdayModalTarget.pet.pet_breed}`}</div>
                <div className="text-velta-mute">Nato il {birthdayModalTarget.pet.pet_birth_date} · Sconto {birthdayModalTarget.discount_pct}%</div>
                <div className="text-velta-mute mt-1">Provider: <strong className="text-velta-ink">{birthdayModalTarget.provider || "wa_deeplink"}</strong> · Destinatario: {birthdayModalTarget.pet.owner_phone}</div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">Anteprima messaggio</label>
                <div className="bg-velta-bg border border-velta-border rounded-md p-3 text-sm font-mono whitespace-pre-line" data-testid="appt-birthday-preview">
                  {birthdayModalTarget.message}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="appt-birthday-send-auto"
                  onClick={() => sendBirthdayNow(birthdayModalTarget.pet.appointment_id, "auto", birthdayModalTarget.already_sent_this_year)}
                  className="h-11 bg-[#EC4899] hover:bg-[#DB2777] text-white rounded-md font-medium btn-tactile inline-flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" /> Invia con provider attivo
                </button>
                {birthdayModalTarget.whatsapp_url && (
                  <a
                    data-testid="appt-birthday-wa-link"
                    href={birthdayModalTarget.whatsapp_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => sendBirthdayNow(birthdayModalTarget.pet.appointment_id, "wa_deeplink", birthdayModalTarget.already_sent_this_year)}
                    className="h-11 bg-[#25D366] text-white rounded-md font-medium btn-tactile inline-flex items-center justify-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" /> Apri WhatsApp <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
              {birthdayModalTarget.already_sent_this_year && (
                <div className="text-[11px] text-velta-success">✓ Auguri già inviati quest&apos;anno — verranno rispediti</div>
              )}
              {!birthdayModalTarget.enabled && (
                <div className="text-[11px] text-velta-warning">⚠️ Sconto disattivato per questo pet — modificalo dalla scheda appuntamento</div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal Storico Animali */}
      {showPets && (
        <PetsListModal
          onClose={() => setShowPets(false)}
          onSelect={(p) => { setShowPets(false); setHistoryPet({ name: p.pet_name, phone: p.owner_phone || "" }); }}
        />
      )}
      {historyPet && (
        <PetHistoryModal petName={historyPet.name} ownerPhone={historyPet.phone} onClose={() => setHistoryPet(null)} />
      )}
    </div>
  );
}
